import { format } from "date-fns";
import moment from "moment";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  formatStatementApiEndDate,
  formatStatementApiStartDate,
  parseAmountInput,
  parseStatementApiFilterDate,
  startOfDay,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";
import {
  flattenTransactionDayGroups,
  getExtractTransactionAmountCents,
  isExtractCreditTransaction,
  regroupTransactionsByDay,
} from "../Extract/extractFilters";
import { getStatementEndToEndId } from "../../../Utils/mapStatementTransactionRow";

export { flattenTransactionDayGroups, regroupTransactionsByDay };

export const EMPTY_PIX_STATEMENT_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  productId: "",
  subAccountId: "",
  searchQuery: "",
};

export const PIX_PRODUCT_OPTIONS = [
  { id: "150", labelKey: "pix.typeReceive" },
  { id: "149", labelKey: "pix.typePayment" },
];

export const getPixProductOptions = () =>
  PIX_PRODUCT_OPTIONS.map((option) => ({
    id: option.id,
    label: intl.get(option.labelKey),
  }));

export const getPixProductLabel = (productId) => {
  const option = PIX_PRODUCT_OPTIONS.find((item) => item.id === String(productId));
  return option ? intl.get(option.labelKey) : productId;
};

export const filtersFromPixFilterObj = (filterObj = {}) => ({
  dateFrom: parseStatementApiFilterDate(filterObj.start_date),
  dateTo: parseStatementApiFilterDate(filterObj.end_date),
  amountMin: filterObj.start_amount
    ? String(Number(filterObj.start_amount) / 100).replace(".", ",")
    : "",
  amountMax: filterObj.end_amount
    ? String(Number(filterObj.end_amount) / 100).replace(".", ",")
    : "",
  productId: filterObj.productId ? String(filterObj.productId) : "",
  subAccountId: filterObj.sub_account_id || "",
  searchQuery: "",
});

export const pixFilterObjFromFilters = (filters) => ({
  start_date: formatStatementApiStartDate(filters.dateFrom),
  end_date: formatStatementApiEndDate(filters.dateTo),
  start_amount: (() => {
    const parsed = parseAmountInput(filters.amountMin);
    return parsed === null ? "" : String(Math.round(parsed * 100));
  })(),
  end_amount: (() => {
    const parsed = parseAmountInput(filters.amountMax);
    return parsed === null ? "" : String(Math.round(parsed * 100));
  })(),
  productId: filters.productId ? Number(filters.productId) : "",
  sub_account_id: filters.subAccountId || "",
});

export const resolvePixStatementFilters = (filters) => filters ?? EMPTY_PIX_STATEMENT_FILTERS;

const normalizePixProductText = (value) => String(value ?? "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

export const getPixTransactionProductId = (item) => {
  const explicitId = item?.product_id ?? item?.productId ?? item?.category?.id ?? item?.category_id;
  if (explicitId != null && explicitId !== "") {
    return String(explicitId);
  }

  const haystack = normalizePixProductText([
    item?.description,
    item?.name,
    item?.category?.name,
  ].filter(Boolean).join(" "));

  if (/\b(recebimento|receber|received|recebido)\b/.test(haystack)) {
    return "150";
  }

  if (
    /\b(pagamento|pagar|payment|pagado)\b/.test(haystack)
    || /\btaxa\b.*\bpix\b/.test(haystack)
    || /\bpix\b.*\btaxa\b/.test(haystack)
  ) {
    return "149";
  }

  return isExtractCreditTransaction(item) ? "150" : "149";
};

export const matchesPixProductFilter = (item, productId) => {
  if (!productId) return true;
  return getPixTransactionProductId(item) === String(productId);
};

export const PIX_RECEIVE_PRODUCT_ID = "150";
export const PIX_PAYMENT_PRODUCT_ID = "149";

/** Ledger category ids (AccountBalanceCategory) used by Extrato / BaaS statements. */
const PIX_LEDGER_CATEGORY_IDS = new Set(["6", "7", "33", "34", "40", "149", "150"]);

/**
 * True for Extrato statement rows that belong on Área Pix.
 * Prefer type/category from statements (not the parallel payments feed).
 */
export const isPixStatementItem = (item) => {
  if (!item || typeof item !== "object") return false;

  const productId = String(
    item.product_id ?? item.productId ?? item.category?.id ?? item.category_id ?? "",
  ).trim();
  if (PIX_LEDGER_CATEGORY_IDS.has(productId)) return true;

  const type = String(item.type ?? "").trim();
  if (/^PIX$/i.test(type) || /^PIX_FEE$/i.test(type)) return true;

  const category = String(item.category?.name ?? "").trim();
  if (/pix/i.test(category) || /qr\s*code/i.test(category) || /qrcode/i.test(category)) {
    return true;
  }

  const description = String(item.description ?? item.name ?? "").trim();
  if (/^PIX\b/i.test(description) || /\bPIX\b/i.test(description)) return true;
  if (/qr\s*code/i.test(description) || /qrcode/i.test(description)) return true;

  return false;
};

export const getPixTransactionEndToEndId = (item) =>
  getStatementEndToEndId(item);

export const isPixRecebimentoTransaction = (item) => {
  if (!item) return false;
  if (item.is_refund || item?.receipt?.type === "refound") return false;
  if (!isExtractCreditTransaction(item)) return false;
  if (!isPixStatementItem(item)) return false;
  if (getPixTransactionProductId(item) !== PIX_RECEIVE_PRODUCT_ID) return false;

  const explicitId = item?.product_id ?? item?.productId ?? item?.category?.id ?? item?.category_id;
  if (explicitId != null && explicitId !== "" && String(explicitId) === PIX_RECEIVE_PRODUCT_ID) {
    return true;
  }

  const haystack = normalizePixProductText([
    item?.description,
    item?.name,
    item?.category?.name,
  ].filter(Boolean).join(" "));

  return /\bpix\b/.test(haystack);
};

/**
 * Shared Estornar eligibility for wallet statement detail footers / row menus.
 * Inbound PIX credit that is not already an estorno row, plus optional UI gate.
 */
export const canRefundPixRecebimento = (item, { refundEnabled = true } = {}) =>
  Boolean(refundEnabled) && isPixRecebimentoTransaction(item);

export const hasPixApiFilters = (filterObj = {}) =>
  Boolean(
    filterObj.start_date
    || filterObj.end_date
    || filterObj.start_amount
    || filterObj.end_amount
    || filterObj.sub_account_id,
  );

export const hasPixClientSideFilters = (filters) => {
  const resolved = resolvePixStatementFilters(filters);
  return Boolean(resolved.productId) || Boolean(resolved.searchQuery?.trim());
};

export const hasActivePixStatementFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.productId !== ""
  || filters.subAccountId !== ""
  || Boolean(filters.searchQuery?.trim());

export const isPixChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "type":
      return filters.productId !== "";
    case "subAccount":
      return filters.subAccountId !== "";
    default:
      return false;
  }
};

export const buildPixStatementFilterTags = (filters, subAccounts = []) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.productId) {
    tags.push({
      key: "productId",
      iconType: "type",
      value: getPixProductLabel(filters.productId),
    });
  }

  if (filters.subAccountId) {
    const subAccount = subAccounts.find((sa) => sa.subAccountId === filters.subAccountId);
    tags.push({
      key: "subAccountId",
      iconType: "subAccount",
      value: subAccount?.name || filters.subAccountId,
    });
  }

  return tags;
};

export const removePixStatementFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "productId":
      return { ...filters, productId: "" };
    case "subAccountId":
      return { ...filters, subAccountId: "" };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const applyPixClientFilters = (items, filters) => {
  const resolved = resolvePixStatementFilters(filters);
  const minAmount = parseAmountInput(resolved.amountMin);
  const maxAmount = parseAmountInput(resolved.amountMax);
  const dateFrom = resolved.dateFrom ? startOfDay(resolved.dateFrom) : null;
  const dateTo = resolved.dateTo ? endOfDay(resolved.dateTo) : null;

  return items.filter((item) => {
    // Área Pix always starts from Extrato statements filtered to Pix / QR.
    if (!isPixStatementItem(item)) return false;

    const itemDate = moment(item.created_at).toDate();

    if (dateFrom && itemDate < dateFrom) return false;
    if (dateTo && itemDate > dateTo) return false;

    const amountCents = getExtractTransactionAmountCents(item);
    if (minAmount !== null && amountCents < Math.round(minAmount * 100)) return false;
    if (maxAmount !== null && amountCents > Math.round(maxAmount * 100)) return false;

    if (resolved.productId && !matchesPixProductFilter(item, resolved.productId)) {
      return false;
    }

    if (resolved.searchQuery?.trim()) {
      const query = resolved.searchQuery.trim().toLowerCase();
      const haystack = [
        item.description,
        item.name,
        item.operation_id,
        item.category?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!haystack.includes(query)) return false;
    }

    return true;
  });
};

export const exportPixStatementCsv = (items) => {
  const headers = [
    intl.get("grid.date"),
    intl.get("grid.entry"),
    intl.get("grid.value"),
    intl.get("extract.filterType"),
  ];

  const rows = items.map((item) => {
    const amount = getExtractTransactionAmountCents(item);
    const isCredit = isExtractCreditTransaction(item);
    return [
      moment(item.created_at).format("DD/MM/YYYY HH:mm"),
      item.description || "",
      amount.toFixed(2).replace(".", ","),
      isCredit ? intl.get("extract.typeCredit") : intl.get("extract.typeDebit"),
    ];
  });

  downloadCsv(headers, rows, `pix-${format(new Date(), "yyyy-MM-dd")}.csv`);
};
